import { useTakeoverDialogStyles } from "../../forms/SearchTakeoverDialog";
import { Button, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { T } from "../../../util/t";
import { Close } from "@mui/icons-material";
import { SearchForm } from "../../../search/search";
import { OptionCheckbox } from "./OptionCheckbox";
import { useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { useSimpleListFilter } from "../../../../hooks/useListFilter";
import { getOptionDiscountPrice, getOptionPrice } from "../paidRegistrationUtils";
import { formatPrice } from "../../../../utils/utils";
import { PriceDescriptions } from "./PriceDescriptions";

export const useEligibleOptions = ({ options, athlete, athletes, index, registrationOptions, paymentOptions }) => {
    const limitExceeded = athlete?.registrations_attributes.length >= (registrationOptions?.divisionLimit || Infinity);
    const showSpots = options.some(division => division.spotsLeft != null);

    const [eligible, ineligible] = options.reduce(([eligible, ineligible], option) => {
        const selectedOption = athlete?.registrations_attributes.find(selectedOption => selectedOption.option_id === option.optionId);
        const registered = !!selectedOption?.id;
        const checked = !!selectedOption;
        const takenSpots = showSpots && athletes.reduce((spots, athlete, athleteIndex) =>
            spots + ((athleteIndex !== index &&
                athlete.registrations_attributes.find(rego => rego.option_id === option.optionId && !rego.id)
            ) ? 1 : 0)
        , 0);
        const spotsLeft = option.spotsLeft && (option.spotsLeft - takenSpots);

        const optionData = registrationOptions?.divisions?.find(d => d.id === option.optionId);
        if (!registered && optionData && (
            (!athlete?.dob && (optionData.minBy || optionData.maxBy)) ||
            (optionData.minBy && athlete?.dob && dayjs(optionData.minBy).diff(athlete?.dob, "year") < optionData.minAge) ||
            (optionData.maxBy && athlete?.dob && dayjs(optionData.maxBy).diff(athlete?.dob, "year") > optionData.maxAge)
        )) {
            ineligible.push({
                optionId: option.optionId,
                name: option.name,
                checked: false,
                disabled: true,
                subLabel: <T>Athlete outside age limit</T>
            });
        } else {
            const disabled = registered || (spotsLeft === 0 || spotsLeft < 0) || (limitExceeded && !selectedOption);
            const subLabel =
                registered ?
                    "Already registered" :
                    showSpots &&
                    (spotsLeft > 0
                        ? <T spots={spotsLeft}>spots_left</T>
                        : (spotsLeft === 0 || spotsLeft < 0)
                            ? <T>No spots left</T>
                            : <T>Unlimited spots</T>
                    );
            eligible.push({
                optionId: option.optionId,
                name: option.name,
                checked,
                disabled,
                subLabel,
                price: paymentOptions && !disabled && formatPrice(getOptionPrice(option.optionId, paymentOptions), paymentOptions.currency),
                discountPrice: paymentOptions && !disabled && formatPrice(getOptionDiscountPrice(athlete?.registrations_attributes, option.optionId, paymentOptions), paymentOptions.currency)
            });
        }

        return [eligible, ineligible];
    }, [[], []]);

    return  [eligible, ineligible];
};

export const SelectOptionsDialog = ({ index, options, registrationOptions, paymentOptions, toggleOption, closeDialog, control, isSeriesRegistration }) => {
    const athletes = useWatch({ name: "athletes", control });
    const [eligible, ineligible] = useEligibleOptions({ options, athlete: athletes[index], athletes, index, registrationOptions, paymentOptions });

    return <SelectOptionsDialogContent
        options={eligible.concat(ineligible)}
        registrationOptions={registrationOptions}
        paymentOptions={paymentOptions}
        toggleOption={toggleOption}
        closeDialog={closeDialog}
        isSeriesRegistration={isSeriesRegistration}
    />;
};

const getOptionLabel = option => option.name;
const SelectOptionsDialogContent = ({ options, closeDialog, registrationOptions, paymentOptions, toggleOption, isSeriesRegistration }) => {
    const {
        inputRef,
        searchString,
        onSearch,
        filteredList
    } = useSimpleListFilter(options, getOptionLabel);
    const showSubtext = registrationOptions?.divisionLimit || paymentOptions;
    const classes = useTakeoverDialogStyles();

    return (
        <>
            <DialogTitle id="divisions-selector-dialog-title">
                <Typography variant="h3" component="div" className={classes.title}>
                    {isSeriesRegistration ?
                        <T>Select membership options</T> :
                        <T>Select divisions</T>
                    }
                    <IconButton color="inherit" size="small" onClick={closeDialog}><Close/></IconButton>
                </Typography>
                {showSubtext &&
                <Typography variant="subtitle2" component="p" color="textSecondary" marginTop={2}>
                    <PriceDescriptions
                        registrationOptions={registrationOptions}
                        isSeriesRegistration={isSeriesRegistration}
                        paymentOptions={paymentOptions}
                        totalDivisions={options.length}
                    />
                </Typography>}
                <Typography component="div" marginTop={2}>
                    <SearchForm inputRef={inputRef} search={searchString} onChange={onSearch}/>
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {filteredList.map(option =>
                    <OptionCheckbox
                        key={option.optionId}
                        id={option.optionId}
                        label={option.name}
                        disabled={option.disabled}
                        subLabel={option.subLabel}
                        checked={option.checked}
                        onChange={e => toggleOption(e, option)}
                        price={option.price}
                        discountPrice={option.discountPrice}
                    />
                )}
            </DialogContent>
            <DialogActions className="sticky-dialog-actions">
                <Button variant="contained" onClick={closeDialog} fullWidth>
                    <T>Done</T>
                </Button>
            </DialogActions>
        </>
    );
};
